import type { ReactNode } from 'react';
import { AiPointerButton, type PointerHelp } from '@belong/ai-pointer';
import { PATHS, formatMoney, formatPercentage, template } from '@belong/common';
import { Text } from '@belong/ui';
import { clsx } from 'clsx';
import {
  type GainOverTime,
  getMaintenanceExpense,
  useGainsOverTime,
  useWealthAnalysis,
} from 'accounts/hooks/wealth-analysis';
import IconButton, { ALIGN_TYPES } from 'components/IconButton/IconButton';
import gradientBorderCss from 'styles/gradient-border.module.css';
import { Sparkline } from './sparkline';
import css from './yearly-wealth-dashboard.module.css';
import {
  YEARLY_WEALTH_DASHBOARD_STRINGS as STRINGS,
  getCashFlowHelp,
  getEstimatedReturnHelp,
  getEstimatedRoiHelp,
  getMaintenanceBudgetHelp,
} from './yearly-wealth-dashboard.strings';

type Props = {
  homeId: string;
  unitNumber?: string;
  className?: string;
};

const GRID_COLUMNS: Record<number, string> = {
  2: 'md:grid-cols-2',
  3: 'md:grid-cols-3',
  4: 'md:grid-cols-4',
};

function getChartPoints(gainsOverTime: GainOverTime[], year: number) {
  return gainsOverTime
    .filter((point) => point.year === year)
    .sort((a, b) => a.month - b.month);
}

type StatProps = {
  value: ReactNode;
  label: string;
  help: PointerHelp;
  detail: string;
  background: ReactNode;
};

function Stat({ value, label, help, detail, background }: StatProps) {
  return (
    <div
      className={clsx(
        'relative overflow-hidden rounded border border-solid',
        gradientBorderCss.gradientBorder,
        css.stat,
      )}
    >
      {background}
      <div className="p-xs md:p-sm relative flex flex-col">
        <Text
          variant="p1"
          className="h-xl whitespace-pre-line md:h-auto md:whitespace-normal"
        >
          {label}
        </Text>
        <div className="gap-2xs flex items-center">
          {value}
          <AiPointerButton inline help={help} />
        </div>
        <Text variant="p1" className="mt-2xs text-dark-gray">
          {detail}
        </Text>
      </div>
    </div>
  );
}

export function YearlyWealthDashboard({
  homeId,
  unitNumber,
  className,
}: Props) {
  const { wealthAnalysis, year } = useWealthAnalysis(homeId);
  const gainsOverTime = useGainsOverTime(homeId, !!wealthAnalysis?.isPublished);

  const model = wealthAnalysis?.belongsViewModel;
  const header = model?.header;

  if (!wealthAnalysis?.isPublished || !model || !header) {
    return null;
  }

  const maintenanceExpense = getMaintenanceExpense(wealthAnalysis);

  const estimatedReturn = formatMoney(header.estimatedReturns, 'THOUSANDS');
  const estimatedRoi = formatPercentage(
    header.estimatedRoiPercentage / 100,
    'NO_DECIMAL',
  );
  const cashFlow = formatMoney(header.averageMonthlyCashFlow);
  const showCashFlow = header.averageMonthlyCashFlow >= 0;
  const statCount = 2 + Number(showCashFlow) + Number(!!maintenanceExpense);

  const returnDetail = template(STRINGS.soFar, {
    value: formatMoney(model.historicalTotalGains, 'THOUSANDS'),
  });
  const roiDetail = template(STRINGS.vsStock, {
    value: formatPercentage(model.compareToStock / 100, 'NO_DECIMAL_SIGNED'),
  });
  const cashFlowDetail = template(STRINGS.soFar, {
    value: formatMoney(model.historicalCashFlow),
  });

  const chartPoints = getChartPoints(gainsOverTime ?? [], year);
  const expensePoints = chartPoints.map((point) => point.totalExpenses);
  const expenseValues = expensePoints.every(
    (value): value is number => value !== undefined,
  )
    ? expensePoints.map(Math.abs)
    : [];

  const title = unitNumber
    ? template(STRINGS['title-unit'], { year, unitNumber })
    : template(STRINGS.title, { year });

  return (
    <section className={clsx('pb-xl', className)}>
      <Text variant="h3" fontWeight="semibold" className="mb-sm">
        {title}
      </Text>
      <div className={clsx('gap-sm grid grid-cols-2', GRID_COLUMNS[statCount])}>
        <Stat
          label={STRINGS.estimatedReturns}
          help={getEstimatedReturnHelp({ year, value: estimatedReturn })}
          background={
            <Sparkline
              id={`${homeId}-estReturnCumulative`}
              values={chartPoints.map((point) => point.estReturnCumulative)}
            />
          }
          detail={returnDetail}
          value={
            <Text variant="h2" fontWeight="semibold">
              {estimatedReturn}
            </Text>
          }
        />
        <Stat
          label={STRINGS.estimatedRoi}
          help={getEstimatedRoiHelp({ year, value: estimatedRoi })}
          background={
            <Sparkline
              id={`${homeId}-roi`}
              values={chartPoints.map((point) => point.roi)}
            />
          }
          detail={roiDetail}
          value={
            <Text variant="h2" fontWeight="semibold">
              {estimatedRoi}
            </Text>
          }
        />
        {showCashFlow && (
          <Stat
            label={STRINGS.averageMonthlyCashFlow}
            help={getCashFlowHelp({ year, value: cashFlow })}
            background={
              <Sparkline
                id={`${homeId}-totalCashFlow`}
                values={chartPoints.map((point) => point.totalCashFlow)}
              />
            }
            detail={cashFlowDetail}
            value={
              <div className="flex items-baseline">
                <Text variant="h2" as="span" fontWeight="semibold">
                  {cashFlow}
                </Text>
                <Text variant="p1" as="span" fontWeight="semibold">
                  {STRINGS.byMonth}
                </Text>
              </div>
            }
          />
        )}
        {maintenanceExpense && (
          <Stat
            label={STRINGS.maintenanceBudget}
            help={getMaintenanceBudgetHelp({
              year,
              value: formatMoney(maintenanceExpense.budget, 'DOLLARS_NO_CENTS'),
            })}
            background={
              <Sparkline
                id={`${homeId}-totalExpenses`}
                values={expenseValues}
              />
            }
            detail={template(STRINGS.spentSoFar, {
              value: formatMoney(maintenanceExpense.spent, 'DOLLARS_NO_CENTS'),
            })}
            value={
              <Text variant="h2" fontWeight="semibold">
                {formatMoney(maintenanceExpense.budget, 'DOLLARS_NO_CENTS')}
              </Text>
            }
          />
        )}
      </div>
      <div className="mt-sm flex justify-end">
        <IconButton
          align={ALIGN_TYPES.LEFT}
          label={STRINGS.viewFinancials}
          href={`${PATHS.HOMEOWNER_ACCOUNT_WEALTH}/${homeId}`}
        />
      </div>
      <div className="mt-sm border-0 border-b border-dashed border-b-gray" />
    </section>
  );
}
